/* Recherche maison Antistatik (tâche F1, 23/10/2026), à la place de l'application Rapid Search.
   - Ouvre le panneau <dialog> de l'en-tête (snippets/ask-recherche-entete.liquid). Sans ce script, la loupe mène à /search.
   - Au fil de la frappe (2 lettres au moins, 160 ms après la dernière touche) : API de recherche prédictive de Shopify,
     rendue par la section ask-recherche-suggestions (produits, recherches proches, collections, pages, conseils).
   - Table de synonymes du thème (snippets/ask-recherche-synonymes.liquid) : jusqu'à 2 raccourcis en haut des résultats
     (« broderie » → broderie sur devis, « patch » → écussons…) et une requête de secours quand les mots tapés ne donnent
     aucun produit.
   - Clavier : Flèche bas depuis le champ pour parcourir les résultats, Flèche haut pour revenir, Échap pour fermer.
   - Liens vers un autre site, un PDF ou un guide : nouvel onglet, avec « (s'ouvre dans un nouvel onglet) » pour les
     lecteurs d'écran (même règle que snippets/ask-verre-cible.liquid). */
(() => {
  if (customElements.get('ask-recherche')) return;

  const MIN = 2;
  const DELAI = 160;
  const SECTION = 'ask-recherche-suggestions';

  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/œ/g, 'oe')
      .replace(/[-'’.,;:!?/()«»"]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const echapper = (s) =>
    String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const hoteSite = location.hostname.replace(/^www\./, '');
  const estExterne = (url, titre) => {
    try {
      const u = new URL(url, location.href);
      const h = u.hostname.replace(/^www\./, '');
      if (!/^https?:$/.test(u.protocol)) return false;
      if (h !== hoteSite && h !== 'antistatik.fr' && !h.endsWith('.myshopify.com')) return true;
      if (/\.pdf($|\?)/i.test(u.pathname)) return true;
      // Pages qui font quitter le parcours (même liste que la règle C12 de layout/theme.liquid) : nouvel onglet.
      if (/^\/pages\/(nous-trouver|comment-utiliser-le-module-de-personnalisation|guide-comparatif-quel-procede-choisir-pour-mon-projet)\/?$/.test(u.pathname)) return true;
    } catch (e) {
      return false;
    }
    return /guide/i.test(titre || '');
  };
  const FLECHE_EXT =
    '<svg class="ask-ico ask-ext" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M14 4h6v6M20 4l-9 9M19 14v4.5A1.5 1.5 0 0 1 17.5 20h-12A1.5 1.5 0 0 1 4 18.5v-12A1.5 1.5 0 0 1 5.5 5H10"/></svg><span class="ask-cache"> (s’ouvre dans un nouvel onglet)</span>';
  const FLECHE =
    '<svg class="ask-ico ask-rico" width="16" height="16" style="width:16px;height:16px" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';

  class AskRecherche extends HTMLElement {
    connectedCallback() {
      this.dlg = this.querySelector('dialog');
      this.ouvrir = this.querySelector('[data-ask-rech-ouvrir]');
      if (!this.dlg || !this.ouvrir || typeof this.dlg.showModal !== 'function') return; // la loupe reste un lien vers /search
      this.form = this.dlg.querySelector('form');
      this.input = this.dlg.querySelector('.ask-rech__input');
      this.effacer = this.dlg.querySelector('[data-ask-rech-effacer]');
      this.corps = this.dlg.querySelector('[data-ask-rech-corps]');
      this.accueil = this.dlg.querySelector('[data-ask-rech-accueil]');
      this.res = this.dlg.querySelector('[data-ask-rech-res]');
      this.statut = this.dlg.querySelector('[data-ask-rech-statut]');
      this.suggestUrl = this.dataset.suggestUrl || '/search/suggest';
      this.cache = new Map();
      this.timer = 0;
      this.ctrl = null;
      this.vue = '';
      this.repliVue = null; // { n: mots tapés normalisés, q: requête de secours affichée }
      try {
        const brut = JSON.parse(this.querySelector('[data-ask-rech-syn]').textContent || '[]');
        this.syn = brut.map((e) => ({ ...e, mn: (e.m || []).map(norm).filter(Boolean) }));
      } catch (e) {
        this.syn = [];
      }

      // Clic sur la loupe et frappe dans le champ : gérés à la fenêtre, en phase de capture (voir plus bas).
      this.dlg.querySelectorAll('[data-ask-rech-fermer]').forEach((b) => b.addEventListener('click', () => this.dlg.close()));
      this.dlg.addEventListener('click', (e) => {
        if (e.target === this.dlg) this.dlg.close(); // clic sur le fond
      });
      this.dlg.addEventListener('close', () => this.onClose());
      // Échap ferme le panneau, même depuis le champ (dans un champ de recherche, Chrome efface d'abord le texte).
      this.dlg.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !e.isComposing) {
          e.preventDefault();
          this.dlg.close();
        }
      });
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
          const l = this.liens();
          if (l.length) {
            e.preventDefault();
            l[0].focus();
          }
        }
      });
      this.effacer.addEventListener('click', () => {
        this.input.value = '';
        this.planifier(true);
        this.input.focus();
      });
      this.form.addEventListener('submit', (e) => {
        if (!this.input.value.trim()) {
          e.preventDefault();
          this.input.focus();
        }
      });
      this.corps.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        const l = this.liens();
        const i = l.indexOf(document.activeElement);
        if (i < 0) return;
        e.preventDefault();
        if (e.key === 'ArrowUp' && i === 0) this.input.focus();
        else l[e.key === 'ArrowDown' ? Math.min(i + 1, l.length - 1) : i - 1].focus();
      });
      window.addEventListener('resize', () => {
        if (this.dlg.open) this.placer();
      });
    }

    liens() {
      return Array.from(this.corps.querySelectorAll('a[href]')).filter((a) => a.offsetParent !== null);
    }

    placer() {
      // Ordinateur : le panneau se pose sous la barre du menu (téléphone : plein écran, en CSS).
      const barre = document.querySelector('.ask-verre .header.ask-bar') || document.querySelector('.header');
      const bas = barre ? barre.getBoundingClientRect().bottom : 0;
      this.dlg.style.setProperty('--ask-rech-top', `${Math.max(12, Math.round(bas + 10))}px`);
    }

    open() {
      document.querySelectorAll('.ask-vd__details[open]').forEach((d) => (d.open = false)); // tiroir du téléphone
      this.placer();
      this.dlg.showModal();
      document.documentElement.classList.add('ask-rech-ouverte');
      this.ouvrir.setAttribute('aria-expanded', 'true');
      this.input.focus({ preventScroll: true });
      if (this.input.value.trim().length >= MIN) this.lancer();
    }

    onClose() {
      document.documentElement.classList.remove('ask-rech-ouverte');
      this.ouvrir.setAttribute('aria-expanded', 'false');
      clearTimeout(this.timer);
      if (this.ctrl) this.ctrl.abort();
    }

    planifier(tout_de_suite) {
      const v = this.input.value.trim();
      this.effacer.hidden = !this.input.value;
      clearTimeout(this.timer);
      if (v.length < MIN) {
        if (this.ctrl) this.ctrl.abort();
        this.montrerAccueil();
        return;
      }
      this.timer = setTimeout(() => this.lancer(), tout_de_suite ? 0 : DELAI);
    }

    // Appelée par le bouclier (snippets/ask-recherche-bouclier.liquid) à l'envoi du formulaire : si le panneau montre les
    // résultats d'une requête de secours pour ces mots-là, c'est elle qu'on envoie à /search (correction après casseur).
    requeteEnvoi(valeur) {
      return this.repliVue && norm(valeur) === this.repliVue.n ? this.repliVue.q : '';
    }

    montrerAccueil() {
      this.vue = '';
      this.repliVue = null;
      this.res.hidden = true;
      this.res.innerHTML = '';
      this.accueil.hidden = false;
      this.statut.textContent = '';
    }

    raccourcis(n) {
      const p = ` ${n} `;
      return this.syn.filter((e) => e.u && e.mn.some((m) => p.includes(` ${m} `))).slice(0, 2);
    }

    async chercher(q) {
      const cle = norm(q);
      if (this.cache.has(cle)) return this.cache.get(cle);
      if (this.ctrl) this.ctrl.abort();
      this.ctrl = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: SECTION,
        'resources[type]': 'product,collection,page,article,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,tag,vendor',
      });
      const r = await fetch(`${this.suggestUrl}?${params}`, { signal: this.ctrl.signal, credentials: 'same-origin' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const html = await r.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const bloc = doc.querySelector('[data-ask-rs]');
      if (!bloc) throw new Error('section absente');
      if (this.cache.size > 40) this.cache.clear();
      this.cache.set(cle, bloc);
      return bloc;
    }

    async lancer() {
      const brut = this.input.value.trim();
      if (brut.length < MIN) return this.montrerAccueil();
      const n = norm(brut);
      if (n === this.vue && !this.res.hidden) return;
      this.res.classList.add('is-chargement');
      let bloc;
      let repli = '';
      try {
        bloc = await this.chercher(brut);
        // Requête de secours de la table de synonymes : seulement si les mots tapés ne donnent aucun produit
        if (bloc.dataset.produits === '0') {
          const e = this.syn.find((s) => s.q && s.mn.includes(n) && norm(s.q) !== n);
          if (e) {
            const b2 = await this.chercher(e.q);
            if (b2.dataset.produits !== '0') {
              bloc = b2;
              repli = e.q;
            }
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        bloc = null;
      }
      if (this.input.value.trim() !== brut) return; // une autre frappe a suivi
      this.afficher(brut, n, bloc, repli);
    }

    afficher(brut, n, bloc, repli) {
      this.vue = n;
      this.repliVue = repli ? { n, q: repli } : null;
      const frag = document.createElement('div');
      const racc = this.raccourcis(n);
      const contenu = bloc ? bloc.cloneNode(true) : null;
      if (contenu) {
        // Pas de raccourci sans précision qui répète une collection déjà proposée (« T-shirts » quand la collection
        // T-shirts est dans les résultats). Un raccourci avec une précision (broderie sur devis, planches DTF…) reste.
        const deja = new Set(Array.from(contenu.querySelectorAll('.ask-rs__chip[href]')).map((a) => new URL(a.getAttribute('href'), location.href).pathname));
        const utiles = racc.filter((e) => e.n || !deja.has(new URL(e.u, location.href).pathname));
        if (utiles.length) frag.appendChild(this.htmlRaccourcis(utiles));
        if (repli) {
          const p = document.createElement('p');
          p.className = 'ask-rs__repli';
          p.innerHTML = `Résultats pour «&nbsp;${echapper(repli)}&nbsp;»`;
          frag.appendChild(p);
        }
        frag.appendChild(contenu);
      } else {
        if (racc.length) frag.appendChild(this.htmlRaccourcis(racc));
        const p = document.createElement('p');
        p.className = 'ask-rs__erreur';
        p.innerHTML = `La recherche ne répond pas pour le moment. <a class="ask-lien" href="${this.form.action}?q=${encodeURIComponent(brut)}">Voir les résultats pour «&nbsp;${echapper(brut)}&nbsp;»</a>`;
        frag.appendChild(p);
      }
      this.res.replaceChildren(...frag.childNodes);
      this.res.classList.remove('is-chargement');
      this.accueil.hidden = true;
      this.res.hidden = false;
      this.corps.scrollTop = 0;
      const total = contenu ? Number(contenu.dataset.total || 0) : 0;
      const prod = contenu ? Number(contenu.dataset.produits || 0) : 0;
      // Message vocal : la partie « et N autres suggestions » n'est dite que si N vaut au moins 1 (après casseur).
      const autres = total - prod;
      const pour = repli || brut;
      let msg = '';
      if (prod > 0) msg = `${prod} produit${prod > 1 ? 's' : ''}`;
      if (autres > 0) msg += `${prod > 0 ? ' et ' : ''}${autres} ${prod > 0 ? `autre${autres > 1 ? 's' : ''} ` : ''}suggestion${autres > 1 ? 's' : ''}`;
      this.statut.textContent = total ? `${msg} pour « ${pour} ». Flèche bas pour les parcourir.` : `Aucun résultat pour « ${brut} ».`;
    }

    htmlRaccourcis(liste) {
      const ul = document.createElement('ul');
      ul.className = 'ask-rs__raccourcis';
      ul.setAttribute('role', 'list');
      ul.setAttribute('aria-label', 'Raccourcis');
      ul.innerHTML = liste
        .map((e) => {
          const ext = estExterne(e.u, e.t);
          return `<li><a class="ask-rs__raccourci" href="${echapper(e.u)}"${ext ? ' target="_blank" rel="noopener"' : ''}><span class="ask-rs__raccico" aria-hidden="true">${FLECHE}</span><span class="ask-rs__racctxt"><b>${echapper(e.t)}${ext ? FLECHE_EXT : ''}</b>${e.n ? `<span>${echapper(e.n)}</span>` : ''}</span></a></li>`;
        })
        .join('');
      return ul;
    }
  }

  customElements.define('ask-recherche', AskRecherche);

  // Les événements de la loupe et des champs sont tenus à l'écart de l'application Rapid Search par un petit script en
  // ligne, inscrit le plus tôt possible (snippets/ask-recherche-entete.liquid) : il appelle open() et planifier().
})();
